import {
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  TableCell as MuiTableCell,
  TableHead as MuiTableHead,
  TableHeadProps as MuiTableHeadProps,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  TableSortLabel,
  Typography,
  makeStyles,
} from "@material-ui/core"
import clsx from "clsx"
import React, { FC, MouseEvent, useState } from "react"
import { useHistory } from "react-router-dom"
import { useRecoilState } from "recoil"
import { useClients } from "../../hooks/useClients"
import { buildClientDetailPath } from "../../hooks/useNavigation"
import {
  ClientDemoData,
  ClientDisconnected,
  Declined,
  Disconnected,
  InviteCodeStatus,
  Pending,
  PractitionerDisconnected,
  AccountDeleted,
} from "../../utils/constants"
import { getComparator, stableSort } from "../../utils/helpers"
import { hideDisconnectedClientsState } from "../../utils/store"
import { colors } from "../../utils/theme"
import { Order } from "../../utils/types"
import ChevronDownIcon from "../atoms/ChevronDownIcon"
import TableCell from "../atoms/TableCell"
import ToolTip from "../atoms/ToolTip"
import ClientMoreButton from "./ClientMoreButton"
import DeclinedHelpIcon from "./DeclinedHelpIcon"
import QuestionnaireScoringHelpIcon from "./QuestionnaireScoringHelpIcon"
import { useAIServices } from "../../hooks/useAIServices"
import PractitionerDisconnectedHelpIcon from "./PractitionerDisconnectedHelpIcon"
import ClientDisconnectedHelpIcon from "./ClientDisconnectedHelpIcon"
import PendingHelpIcon from "./PendingHelpIcon"
import AccountDeletedHelpIcon from "./AccountDeletedHelpIcon"
import QuestionnaireScore from "./QuestionnaireScore"
import ClientTooltip from "../atoms/tooltip/ClientTooltip"

interface Column {
  id: string
  label: string
  sortable: boolean // controls whether the column can be used to sort the table
}

type TableHeadProps = {
  order: Order
  orderBy: string
  onRequestSort: (event: MouseEvent<unknown>, property: string) => void
  uniqueQuestionnaires: string[]
} & MuiTableHeadProps

const TableHead: FC<TableHeadProps> = ({
  order,
  orderBy,
  onRequestSort,
  uniqueQuestionnaires,
}) => {
  const classes = useStyles()
  const createSortHandler =
    (property: string) => (event: MouseEvent<unknown>) => {
      onRequestSort(event, property)
    }
  const questionnaireColumns = uniqueQuestionnaires.map((questionnaire) => ({
    id: questionnaire,
    label: questionnaire,
    sortable: false,
  }))
  const columns: Column[] = [
    {
      id: "lastName",
      label: "Last Name",
      sortable: true,
    },
    {
      id: "firstName",
      label: "First Name",
      sortable: true,
    },
    {
      id: "displayName",
      label: "Email",
      sortable: true,
    },
    {
      id: "dailyCheckins",
      label: "Daily Check-Ins",
      sortable: true,
    },
    ...questionnaireColumns,
    {
      id: "more",
      label: "",
      sortable: false,
    },
  ]
  return (
    <MuiTableHead>
      <TableRow>
        {columns.map((column) => (
          <MuiTableCell
            key={column.id}
            sortDirection={orderBy === column.id ? order : false}
            className={classes.tableHeaderCell}
            align={column.label === "" ? "right" : undefined}
            padding={column.label === "" ? "none" : undefined}
          >
            {column.sortable ? (
              <TableSortLabel
                active={orderBy === column.id}
                direction={orderBy === column.id ? order : "asc"}
                onClick={createSortHandler(column.id)}
                IconComponent={(props) => <ChevronDownIcon {...props} />}
                classes={{
                  root: classes.tableSortLabel,
                  active: classes.tableSortLabel,
                }}
              >
                <>{column.label}</>
              </TableSortLabel>
            ) : column.label === "" ? (
              <QuestionnaireScoringHelpIcon />
            ) : column.label.length > 12 ? (
              <ToolTip text={column.label}>
                {column.label.slice(0, 12) + "..."}
              </ToolTip>
            ) : (
              <>{column.label}</>
            )}
          </MuiTableCell>
        ))}
      </TableRow>
    </MuiTableHead>
  )
}

/** Displays clients with an in-memory filter preference for the login session. */
export default function ClientTable() {
  const classes = useStyles()
  const [order, setOrder] = useState<Order>("asc")
  const [orderBy, setOrderBy] = useState<string>("lastName")
  const [hideDisconnected, setHideDisconnected] = useRecoilState(
    hideDisconnectedClientsState
  )
  const history = useHistory()
  const { clients, uniqueQuestionnaires } = useClients()
  const { isAIServicesMember } = useAIServices()
  const visibleClients = clients?.filter(
    (client) =>
      !hideDisconnected ||
      (client.more.data.status !== InviteCodeStatus.PRACTITIONER_DISCONNECTED &&
        client.more.data.status !== InviteCodeStatus.CLIENT_DISCONNECTED)
  )

  // Refer to BRD, AI Services Part 1 (Demonstration)
  const clientDescriptions = new Map()

  for (let client of clients ?? []) {
    if (client.displayName.data === "Alex_Davis@gmail.com") {
      clientDescriptions.set(client.id.data, ClientDemoData.ALEX)
    } else if (client.displayName.data === "Susan_James@gmail.com") {
      clientDescriptions.set(client.id.data, ClientDemoData.SUSAN)
    } else if (client.displayName.data === "Janice_Ng@gmail.com") {
      clientDescriptions.set(client.id.data, ClientDemoData.JANICE)
    } else if (client.displayName.data === "Luca_Rodriguez@gmail.com") {
      clientDescriptions.set(client.id.data, ClientDemoData.LUCA)
    }
  }

  /**
   * An array of objects containing the client's id and their questionnaire scores as components
   * Since arrays are stable in Type/JavaScript, we don't need to worry about the order
   * of the questionnaire scores changing between each client row.
   */
  const clientsWithQuestionnaireRows =
    clients?.map((client) => {
      const questionnaireRows = uniqueQuestionnaires.map((questionnaire) => {
        const questionnaireData = (
          client.questionnaires.data as unknown as any[]
        ).find(
          (questionnaireData) =>
            questionnaireData.data.questionnaire_name === questionnaire
        )

        const displayScore = questionnaireData?.data.display_score
        if (displayScore == null) {
          return null
        }

        return (
          <QuestionnaireScore
            key={questionnaire}
            score={displayScore}
            scoreType={questionnaireData?.data.display_score_type ?? ""}
            severityColor={questionnaireData?.data.scoring_display_color}
            severityLabel={questionnaireData?.data.scoring_display_name ?? ""}
            date={questionnaireData?.data.completed_at ?? ""}
          />
        )
      })
      return { id: client.id.data, questionnaireRows }
    }) ?? []

  /**
   * Get the questionnaire score components for a client
   * @param clientId the client's id
   * @returns an array of questionnaire score components
   */
  function getQuestionnaireScores(clientId: string) {
    return clientsWithQuestionnaireRows.find(
      (client) => client.id === clientId && client.questionnaireRows
    )?.questionnaireRows
  }
  return (
    <>
      <div className={classes.tableControls}>
        <FormControlLabel
          control={
            <Switch
              checked={hideDisconnected}
              onChange={(_, checked) => setHideDisconnected(checked)}
              color="primary"
            />
          }
          label="Hide disconnected clients"
        />
      </div>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table className={classes.table}>
          <TableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={(
              event: React.MouseEvent<unknown>,
              property: string
            ) => {
              const isAsc = orderBy === property && order === "asc"
              setOrder(isAsc ? "desc" : "asc")
              setOrderBy(property)
            }}
            uniqueQuestionnaires={uniqueQuestionnaires}
          />
          {visibleClients && visibleClients.length > 0 && (
            <TableBody>
              {stableSort(visibleClients, getComparator(order, orderBy)).map(
                (row, _) => {
                  const onClick = () => {
                    if (
                      [
                        InviteCodeStatus.CONNECTED,
                        InviteCodeStatus.PRACTITIONER_DISCONNECTED,
                        InviteCodeStatus.CLIENT_DISCONNECTED,
                      ].includes(row.more.data.status)
                    ) {
                      history.push(buildClientDetailPath(row.id.data))
                    }
                  }
                  const desc = clientDescriptions.get(row.id.data) ?? ""
                  return (
                    <TableRow key={row.id.data}>
                      <TableCell
                        className={
                          typeof row.dailyCheckins.data !== "number"
                            ? classes.pending
                            : undefined
                        }
                        role="button"
                        onClick={onClick}
                      >
                        {row.lastName.data}
                      </TableCell>
                      <TableCell
                        className={
                          typeof row.dailyCheckins.data !== "number"
                            ? classes.pending
                            : undefined
                        }
                        role="button"
                        onClick={onClick}
                      >
                        {isAIServicesMember && desc ? (
                          <ClientTooltip desc={desc}>
                            <div>{row.firstName.data}</div>
                          </ClientTooltip>
                        ) : (
                          <div>{row.firstName.data}</div>
                        )}
                      </TableCell>
                      <TableCell
                        className={
                          typeof row.dailyCheckins.data !== "number"
                            ? classes.pending
                            : undefined
                        }
                        role="button"
                        onClick={onClick}
                      >
                        {row.displayName.data}
                      </TableCell>
                      <TableCell
                        className={
                          typeof row.dailyCheckins.data !== "number"
                            ? classes.pending
                            : undefined
                        }
                        role="button"
                        onClick={onClick}
                      >
                        <div
                          className={
                            [
                              Declined,
                              Disconnected,
                              PractitionerDisconnected,
                              ClientDisconnected,
                              Pending,
                              AccountDeleted,
                            ].includes(row.dailyCheckins.data)
                              ? classes.helpIconContainer
                              : undefined
                          }
                        >
                          {typeof row.dailyCheckins.data !== "number"
                            ? row.dailyCheckins.data
                            : row.dailyCheckins.data < 10
                            ? "<10"
                            : row.dailyCheckins.data >= 10 &&
                              row.dailyCheckins.data < 40
                            ? "10-39"
                            : "40+"}
                          {row.dailyCheckins.data === Pending && (
                            <div className={classes.helpIcon}>
                              <PendingHelpIcon
                                created_at={row.more.data.created_at}
                              />
                            </div>
                          )}
                          {row.dailyCheckins.data === Declined && (
                            <div className={classes.helpIcon}>
                              <DeclinedHelpIcon
                                ended_at={row.more.data.ended_at}
                              />
                            </div>
                          )}
                          {row.dailyCheckins.data === ClientDisconnected && (
                            <div className={classes.helpIcon}>
                              <ClientDisconnectedHelpIcon
                                ended_at={row.more.data.ended_at}
                              />
                            </div>
                          )}
                          {row.dailyCheckins.data ===
                            PractitionerDisconnected && (
                            <div className={classes.helpIcon}>
                              <PractitionerDisconnectedHelpIcon
                                ended_at={row.more.data.ended_at}
                              />
                            </div>
                          )}
                          {row.dailyCheckins.data === AccountDeleted && (
                            <div className={classes.helpIcon}>
                              <AccountDeletedHelpIcon
                                ended_at={row.more.data.ended_at}
                              />
                            </div>
                          )}
                        </div>
                      </TableCell>
                      {getQuestionnaireScores(row.id.data)?.map(
                        (score, scoreIndex) => {
                          return (
                            <TableCell
                              role="button"
                              onClick={onClick}
                              borderLeft={true}
                              align="center"
                              key={scoreIndex}
                            >
                              {score}
                            </TableCell>
                          )
                        }
                      )}
                      <TableCell
                        align="center"
                        padding="none"
                        borderLeft={true}
                      >
                        <ClientMoreButton
                          code={row.more.data.code}
                          status={row.more.data.status}
                          clientId={row.id.data}
                          firstName={row.firstName.data}
                          lastName={row.lastName.data}
                          displayName={row.displayName.data}
                        />
                      </TableCell>
                    </TableRow>
                  )
                }
              )}
            </TableBody>
          )}
        </Table>
      </TableContainer>

      {clients && clients.length > 0 && visibleClients?.length === 0 && (
        <div className={classes.noData}>
          <Typography variant="body1">
            No clients match the current filter.
          </Typography>
        </div>
      )}

      {clients && clients.length === 0 && (
        <div className={classes.noData}>
          <Typography variant="body1" className={classes.noDataFirstLine}>
            You currently do not have any clients listed on your dashboard.
          </Typography>
          <Typography variant="body1">
            To add your first client, select “Add Client” in the upper left and
            input the code found in your client’s Vira app.
          </Typography>
        </div>
      )}

      {!clients && (
        <div className={clsx(classes.noData, classes.center)}>
          <CircularProgress />
        </div>
      )}
    </>
  )
}

const useStyles = makeStyles((theme) => ({
  tableControls: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(1),
  },
  tableContainer: {
    width: "100%",
    overflowY: "visible",
    boxShadow: "none",
    backgroundColor: "transparent",
  },
  table: {
    color: colors.neutral.denim,
    borderRadius: 0,
    boxShadow: "none",
    border: `1px solid ${colors.neutral.clay}`,
    backgroundColor: colors.neutral.white,
    minWidth: "100%",
    width: "max-content",
  },
  tableHeaderCell: {
    color: colors.neutral.denim,
    fontWeight: 700,
    borderBottom: `1px solid ${colors.neutral.clay}`,
  },
  tableSortLabel: {
    color: `${colors.neutral.denim} !important`,
    fontWeight: 700,
  },
  noData: {
    width: "100%",
    padding: 27,
    textAlign: "center",
    border: `1px solid ${colors.neutral.clay}`,
    borderTop: 0,
    backgroundColor: colors.neutral.white,
  },
  noDataFirstLine: {
    fontWeight: 700,
    marginBottom: 6,
  },
  pending: {
    color: colors.neutral.asphalt,
    fontStyle: "italic",
  },
  center: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  helpIconContainer: {
    display: "flex",
    alignItems: "center",
  },
  helpIcon: {
    marginLeft: theme.spacing(2),
    height: 24,
  },
}))
